import {
    Directive,
    EventEmitter,
    HostListener,
    Input,
    Output,
    SimpleChanges,
    Injector,
    OnInit,
    OnDestroy,
    AfterViewInit,
} from '@angular/core';
import { eventFindInPath, fiDebounce } from '@fi-shared/utils';
import { TextEditorHistoryService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/text-editor-history.service';
import { TextEditorInsertLinkService } from '@fi-shared/fi-text-editor/fi-text-editor-insert-link/text-editor-insert-link.service';
import { AdvancedTextHandlerService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/advanced-text-handler.service';
import { TextEditorSelectionElementBuilderService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/text-editor-selection-element-builder.service';
import { TextEditorInsertEmbedCodeService } from '@fi-shared/text-editor/text-editor-insert-embed-code.service';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FiTextItemHelperService } from '@fi-shared/fi-text-item-helper/fi-text-item-helper.service';
import { isDefined } from '@fi-shared/utils/is-defined';
import { FiContenteditableBase } from '@fi-shared/fi-contenteditable';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { Subject } from 'rxjs';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { FiContenteditableService } from '@fi-shared/fi-contenteditable/fi-contenteditable.service';
import { takeUntil } from 'rxjs/operators';
import { AdvancedTextPanelMediaLinkService } from '@fi-shared/fi-text-panel/text-panel-services/advanced-text-panel-media-link.service';
import { TextEditorInsertMediaService } from './../text-editor/text-editor-insert-media.service';

@Directive({
    selector: '[fiContenteditableModel]',
    exportAs: 'fiContenteditableModel',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: ContenteditableModelDirective,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: ContenteditableModelDirective,
            multi: true,
        },
    ],
    standalone: false,
})
export class ContenteditableModelDirective extends FiContenteditableBase implements OnInit, AfterViewInit, OnDestroy {
    @Input('fiContenteditableModel') ngModel: string; // todo use [ngModel] input
    @Output('fiContenteditableModelChange') onUpdate = new EventEmitter<string>();

    @Input() closeInsertMediaDialogAfterFileSelection: boolean;
    @Input() mediaBankFileReferences: IMediaBankFileReference[];

    @Input() disableFormattingOnPaste: boolean;
    @Output() afterFirstRender = new EventEmitter();

    public lastRange: Range;

    //private mediaReferencesColumn: number;

    constructor(
        private textEditorHistoryService: TextEditorHistoryService,
        private textEditorInsertLinkService: TextEditorInsertLinkService,
        private advancedTextHandlerService: AdvancedTextHandlerService,
        private textEditorSelectionElementBuilder: TextEditorSelectionElementBuilderService,
        private textEditorInsertEmbedCodeService: TextEditorInsertEmbedCodeService,
        private fiTextItemHelperService: FiTextItemHelperService,
        injector: Injector,
        private fiBrowserEventsService: FiBrowserEventsService,
        private fiContenteditableService: FiContenteditableService,
        private advancedTextPanelMediaLinkService: AdvancedTextPanelMediaLinkService,
        private textEditorInsertMediaService: TextEditorInsertMediaService,
    ) {
        super(injector);

        //this.mediaReferencesColumn = this.textItemMediaReferencesService.getNextMediaColumnIdx();
    }

    ngOnInit(): void {
        this.fiBrowserEventsService
            .getWindowEventSubject$('resize')
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => this.onResize());

        this.fiTextItemHelperService.subscribeOnVimeoUpdate(this.elRef.nativeElement, this.ngOnDestroy$, () =>
            this.checkContentChange(),
        );

        this.updateVimeoPlaceholders();
    }

    ngAfterViewInit(): void {
        this.checkCleanHTML();
        this.checkContentChange();
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    doPropagateChanges() {
        super.doPropagateChanges();
        this.onUpdate.next(this.ngModel);
        this.doPropagateChangesDebounced(); // costly operation, should perform deferred
    }

    private doPropagateChangesDebounced = fiDebounce(() => {
        this.updateMediaReferences();
        this.updateTemplateItemTextImageSources();
    }, 1000);

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);

        const change = changes.ngModel;
        if (change) {
            // fist change got previousValue undefined, the second time previousValue === null
            if (
                change.previousValue === null &&
                change.currentValue !== null &&
                changes.model &&
                isDefined(changes.model.currentValue)
            ) {
                this.afterFirstRender.emit();
            }

            if (change.isFirstChange() && this.mediaBankFileReferences) {
                setTimeout(() => {
                    this.fiTextItemHelperService.updateTemplateItemTextImage(
                        this.elRef.nativeElement,
                        this.mediaBankFileReferences,
                    );
                    /*this.textItemMediaReferencesService.removeNonExistingMediaReferences(this.elRef.nativeElement, this.mediaReferences);
                    // add column property to mediaReferences (it is shared array, so we should change only its content and do not reassign variable)
                    this.textItemMediaReferencesService.updateMediaReferences(this.elRef.nativeElement, this.mediaReferences, this.mediaReferencesColumn);
                    setTimeout(() => {
                        // on this step all items should have only MediaBankFileId and column, and we should remove original data
                        const existingReferences = this.mediaReferences.filter(item => !isDefined(item.column));
                        existingReferences.forEach(item => {
                            this.mediaReferences.splice(this.mediaReferences.indexOf(item), 1);
                        });
                    });*/
                });
            }
        }
    }

    private updateVimeoPlaceholders() {
        this.textEditorInsertMediaService.replaceVimeoNotAvailableLabelsInHtml(this.elRef.nativeElement);
    }

    elementOnBlur() {
        super.elementOnBlur();
        this.saveCurrentRange();
    }

    private saveCurrentRange() {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
            this.lastRange = selection.getRangeAt(0);
            this.textEditorSelectionElementBuilder.setCurrentRange(this.lastRange);
        }
    }

    protected checkContentChange() {
        super.checkContentChange();
        this.saveCurrentRange();
    }

    protected refreshView() {
        super.refreshView();
        this.updateTemplateItemTextImageSources();
    }

    @HostListener('focus')
    public onFocus() {
        if (!this.elRef.nativeElement.hasAttribute('id')) {
            this.elRef.nativeElement.setAttribute('id', this.textEditorHistoryService.getContentEditableElementUid());
        }
        this.textEditorHistoryService.setElement(this.elRef.nativeElement);
        this.advancedTextHandlerService.setCurrent(this.elRef.nativeElement);
    }

    // #531: Pasted text needs to be cleared from all formatting
    onPaste(e: ClipboardEvent) {
        this.fiContenteditableService.onPaste(e, { disableFormatting: this.disableFormattingOnPaste });
    }

    @HostListener('copy', ['$event'])
    onCopy(e: ClipboardEvent) {
        this.fiContenteditableService.onCopy(e);
    }

    @HostListener('click', ['$event'])
    onClick(e: MouseEvent) {
        this.handleMediaClick(e) || this.handleLinkClick(e) || this.handleEmbedClick(e);
        this.advancedTextHandlerService.onClick$.next(e);
        this.saveCurrentRange();
    }

    onKeyup(e) {
        super.onKeyup(e);
        this.advancedTextHandlerService.onKeyup$.next(e);
    }

    private handleMediaClick(e: MouseEvent): boolean {
        return false;
        /*const media = eventFindInPath(e, '[ccm-text-item-media-model]');
        if (!media) {
            return false
        }
        e.preventDefault();
        const range = selectItem(media);
        this.insertMediaService.openEditMediaDialog(media).onYesButtonClick((result) => {
            if (!result.file) {
                range.deleteContents();
                this.checkContentChange();
                return;
            }
            const mediaServiceResult = JSON.parse(JSON.stringify(result));
            mediaServiceResult.selectedFiles = [mediaServiceResult.file];
            delete mediaServiceResult.file;
            this.textEditorInsertMediaService.createNodesFromResult(mediaServiceResult).then((nodes) => {
                range.deleteContents();
                if (nodes) {
                    nodes.forEach((node) => {
                        range.insertNode(node);
                    });
                }
                this.checkContentChange();
            });
        });
        return true;*/
    }

    private handleLinkClick($event: MouseEvent): boolean {
        const link = eventFindInPath($event, 'ccm-text-item-link') || eventFindInPath($event, 'a[ccm-text-item-link]');
        if (!link) return false;

        $event.preventDefault();
        const ccmTextItemLink = JSON.parse(link.getAttribute('text-item-model'));
        if (!isDefined(ccmTextItemLink)) return false;

        const range = new Range();
        range.selectNode(link.firstChild);
        this.advancedTextPanelMediaLinkService.setTextEditorActiveButtons({ link: true });
        this.textEditorInsertLinkService
            .insertLink({
                parentElement: link.parentElement,
                linkToType: ccmTextItemLink.Type,
                range: range,
            })
            .afterClosed(() => this.advancedTextPanelMediaLinkService.setTextEditorActiveButtons({ link: false }));
        this.checkContentChange();
        return true;
    }

    private handleEmbedClick(e: MouseEvent): boolean {
        const embed = eventFindInPath(e, 'ccm-text-item-embed');
        if (!embed) {
            return false;
        }
        this.textEditorInsertEmbedCodeService.insertEmbedCode(embed, {
            model: JSON.parse(embed.getAttribute('text-item-model')),
        });
        this.checkContentChange();
        return true;
    }

    private updateMediaReferences() {
        /*if (!this.mediaReferences || this.mediaReferencesColumn === null || !isDefined(this.mediaReferencesColumn)) {
            return;
        }

        // update references from editor block
        this.textItemMediaReferencesService.updateMediaReferences(this.elRef.nativeElement, this.mediaReferences, this.mediaReferencesColumn);*/
    }

    /*@HostListener('window:resize')*/ // HostListener raises change detection cycle, bad for performance
    onResize() {
        this.updateTemplateItemTextImageSourcesDebounced();
    }

    private updateTemplateItemTextImageSourcesDebounced = fiDebounce(
        () => this.updateTemplateItemTextImageSources(),
        1000,
    );
    private updateTemplateItemTextImageSources() {
        this.fiTextItemHelperService.updateTextItemMediaImages(this.elRef.nativeElement, this.mediaBankFileReferences);
    }
}
